/**
 * Factory unico de query keys.
 *
 * Regla del plan (3.4): **sin arrays inline en ningun componente** y **los
 * filtros siempre en el ultimo segmento**, derivados de la URL. Asi
 * `invalidateQueries({ queryKey: qk.tasks.list(projectId) })` alcanza a todas
 * las combinaciones de filtros de ese proyecto sin enumerarlas.
 *
 * Los arboles interno y del portal estan SEPARADOS a proposito: comparten
 * entidad pero no schema ni permisos, y mezclarlos dejaria cache interna viva
 * en una sesion de cliente.
 */

export type Filters = Record<string, unknown> | undefined

const clean = (filters: Filters): Record<string, unknown> => {
  if (!filters) return {}
  return Object.fromEntries(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .sort(([a], [b]) => a.localeCompare(b)),
  )
}

export const queryKeys = {
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    list: (filters?: Filters) => [...queryKeys.companies.lists(), clean(filters)] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (companyId: string) => [...queryKeys.companies.details(), companyId] as const,
    contacts: (companyId: string) =>
      [...queryKeys.companies.detail(companyId), 'contacts'] as const,
    techStack: (companyId: string) =>
      [...queryKeys.companies.detail(companyId), 'tech-stack'] as const,
    accesses: (companyId: string, filters?: Filters) =>
      [...queryKeys.companies.detail(companyId), 'accesses', clean(filters)] as const,
    documents: (companyId: string, filters?: Filters) =>
      [...queryKeys.companies.detail(companyId), 'documents', clean(filters)] as const,
    projects: (companyId: string, filters?: Filters) =>
      [...queryKeys.companies.detail(companyId), 'projects', clean(filters)] as const,
  },

  portfolio: {
    all: ['portfolio'] as const,
    metrics: () => [...queryKeys.portfolio.all, 'metrics'] as const,
    insights: () => [...queryKeys.portfolio.all, 'metrics', 'insights'] as const,
    kpis: (filters?: Filters) => [...queryKeys.portfolio.all, 'kpis', clean(filters)] as const,
    table: (filters?: Filters) => [...queryKeys.portfolio.all, 'table', clean(filters)] as const,
    priorities: (filters?: Filters) =>
      [...queryKeys.portfolio.all, 'priorities', clean(filters)] as const,
  },

  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters?: Filters) => [...queryKeys.projects.lists(), clean(filters)] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (projectId: string) => [...queryKeys.projects.details(), projectId] as const,
    activity: (projectId: string, filters?: Filters) =>
      [...queryKeys.projects.detail(projectId), 'activity', clean(filters)] as const,
    documents: (projectId: string, filters?: Filters) =>
      [...queryKeys.projects.detail(projectId), 'documents', clean(filters)] as const,
    repositories: (projectId: string) =>
      [...queryKeys.projects.detail(projectId), 'repositories'] as const,
    onboarding: (projectId: string) =>
      [...queryKeys.projects.detail(projectId), 'onboarding'] as const,
    components: (projectId: string) =>
      [...queryKeys.projects.detail(projectId), 'components'] as const,
    componentLinks: (projectId: string) =>
      [...queryKeys.projects.detail(projectId), 'component-links'] as const,
  },

  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (projectId: string, filters?: Filters) =>
      [...queryKeys.tasks.lists(), projectId, clean(filters)] as const,
    board: (projectId: string, filters?: Filters) =>
      [...queryKeys.tasks.all, 'board', projectId, clean(filters)] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (taskId: string) => [...queryKeys.tasks.details(), taskId] as const,
    mine: (filters?: Filters) => [...queryKeys.tasks.all, 'mine', clean(filters)] as const,
  },

  dueSoon: {
    all: ['due-soon'] as const,
    list: (filters?: Filters) => [...queryKeys.dueSoon.all, 'list', clean(filters)] as const,
  },

  support: {
    all: ['support'] as const,
    tickets: (filters?: Filters) => [...queryKeys.support.all, 'tickets', clean(filters)] as const,
    apps: () => [...queryKeys.support.all, 'apps'] as const,
    reporters: () => [...queryKeys.support.all, 'reporters'] as const,
    leaders: () => [...queryKeys.support.all, 'leaders'] as const,
    appStatuses: () => [...queryKeys.support.all, 'app-statuses'] as const,
    appStatus: (appId: string) => [...queryKeys.support.all, 'app-statuses', appId] as const,
    manualStructure: () => [...queryKeys.support.all, 'manual-structure'] as const,
  },

  accesses: {
    all: ['accesses'] as const,
    lists: () => [...queryKeys.accesses.all, 'list'] as const,
    list: (companyId: string, filters?: Filters) =>
      [...queryKeys.accesses.lists(), companyId, clean(filters)] as const,
    detail: (accessId: string) => [...queryKeys.accesses.all, 'detail', accessId] as const,
    // Ojo: el SECRETO nunca tiene query key. Se pide con `useMutation`, vive en
    // un `useState` local y se limpia a los 30s (plan 3.5).
    auditLog: (accessId: string, filters?: Filters) =>
      [...queryKeys.accesses.detail(accessId), 'audit', clean(filters)] as const,
  },

  architecture: {
    all: ['architecture'] as const,
    /** Las plantillas son seeds del sistema: no dependen de empresa ni proyecto. */
    templates: () => [...queryKeys.architecture.all, 'templates'] as const,
  },

  documents: {
    all: ['documents'] as const,
    detail: (documentId: string) => [...queryKeys.documents.all, 'detail', documentId] as const,
    versions: (documentId: string) =>
      [...queryKeys.documents.detail(documentId), 'versions'] as const,
  },

  settings: {
    all: ['settings'] as const,
    stageSets: (filters?: Filters) =>
      [...queryKeys.settings.all, 'stage-sets', clean(filters)] as const,
    users: (filters?: Filters) => [...queryKeys.settings.all, 'users', clean(filters)] as const,
    agents: () => [...queryKeys.settings.all, 'agents'] as const,
    notifications: () => [...queryKeys.settings.all, 'notifications'] as const,
  },

  /**
   * Arbol del portal: fisicamente separado del interno.
   *
   * **Todas las claves aceptan filtros y los ponen en el ultimo segmento**, que
   * es la misma regla del resto del factory. El filtro del portal es siempre el
   * mismo, `asCompany` (el "ver como cliente" del admin), y antes ocho de las
   * nueve lo concatenaban a mano —`[...qk.portal.me(), asCompany]`— porque la
   * factory no lo aceptaba. Funcionaba por prefix matching, pero dejaba "sin
   * impersonar" escrito de dos formas distintas: `[..., null]` en ocho y
   * `[..., {}]` en la unica que si pasaba por el slot.
   */
  portal: {
    all: ['portal'] as const,
    me: (filters?: Filters) => [...queryKeys.portal.all, 'me', clean(filters)] as const,
    overview: (filters?: Filters) => [...queryKeys.portal.all, 'overview', clean(filters)] as const,
    company: (filters?: Filters) => [...queryKeys.portal.all, 'company', clean(filters)] as const,
    projects: (filters?: Filters) => [...queryKeys.portal.all, 'projects', clean(filters)] as const,
    /**
     * El proyecto. `projects()` sin filtros es el PREFIJO de sus hijos (tareas,
     * documentos, arquitectura), asi que los filtros del detalle van en el
     * ultimo segmento de cada hijo, nunca en el medio.
     */
    project: (projectId: string, filters?: Filters) =>
      [...queryKeys.portal.all, 'project', projectId, clean(filters)] as const,
    projectScope: (projectId: string) => [...queryKeys.portal.all, 'project', projectId] as const,
    projectTasks: (projectId: string, filters?: Filters) =>
      [...queryKeys.portal.projectScope(projectId), 'tasks', clean(filters)] as const,
    projectDocuments: (projectId: string, filters?: Filters) =>
      [...queryKeys.portal.projectScope(projectId), 'documents', clean(filters)] as const,
    architecture: (projectId: string, filters?: Filters) =>
      [...queryKeys.portal.projectScope(projectId), 'architecture', clean(filters)] as const,
    document: (documentId: string, filters?: Filters) =>
      [...queryKeys.portal.all, 'document', documentId, clean(filters)] as const,
    board: (filters?: Filters) => [...queryKeys.portal.all, 'board', clean(filters)] as const,
  },
} as const

/** Alias corto para los imports. */
export const qk = queryKeys

export type QueryKeys = typeof queryKeys
